import React from 'react';
import {
  Box,
  Button,
  Flex,
  Icon,
  Radio,
  RadioGroup,
  SimpleGrid,
  Text,
  Tooltip,
  VisuallyHidden,
} from '@chakra-ui/react';
import { FaCheckCircle, FaLock } from 'react-icons/fa';
import PropTypes from 'prop-types';

/**
 * Shared building blocks for the two invoice dialogs (RAT-1826), so the bulk and
 * the single-merchant dialog stay identical in look and behaviour.
 */

/** Field label used above every control in the invoice dialogs. */
export const FieldLabel = ({ children, ...rest }) => (
  <Text fontSize="sm" fontWeight="600" mb={2} color="gray.700" {...rest}>
    {children}
  </Text>
);
FieldLabel.propTypes = { children: PropTypes.node };

/**
 * Segmented control: the selected segment is tinted and outlined, the rest stay
 * quiet. Used for the legal-info state and for Quarters / Months.
 */
export const SegmentedTabs = ({ options, value, onChange }) => (
  <Flex
    direction={{ base: 'column', sm: 'row' }}
    bg="gray.50"
    border="1px solid"
    borderColor="gray.200"
    borderRadius="12px"
    p="4px"
    gap="4px"
  >
    {options.map((option) => {
      const isActive = option.value === value;
      return (
        <Button
          key={option.value}
          type="button"
          flex={{ base: 'none', sm: '1' }}
          w={{ base: '100%', sm: 'auto' }}
          h={{ base: '38px', md: '40px' }}
          px={2}
          borderRadius="8px"
          border="1px solid"
          variant="unstyled"
          bg={isActive ? 'green.50' : 'transparent'}
          borderColor={isActive ? 'reviveGreen' : 'transparent'}
          color={isActive ? 'gray.800' : 'gray.500'}
          _hover={{ bg: isActive ? 'green.50' : 'gray.100' }}
          fontSize={{ base: 'xs', md: 'sm' }}
          fontWeight="600"
          whiteSpace="normal"
          textAlign="center"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={2}
          onClick={() => onChange(option.value)}
        >
          {option.icon && (
            <Icon
              as={option.icon}
              w="15px"
              h="15px"
              color={option.iconColor ?? 'currentColor'}
            />
          )}
          {option.label}
        </Button>
      );
    })}
  </Flex>
);
SegmentedTabs.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType,
      iconColor: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

/**
 * "Select Action" - one card per action, single choice. Exactly one action runs
 * per Proceed, so a preview and an official invoice can never be produced for the
 * same period in one click.
 *
 * Each card states what it does in one line, and an action that cannot run right
 * now carries a lock plus the short reason why (the full sentence stays in the
 * tooltip). The selected card is outlined and check-marked instead of showing a
 * radio dial, so the state is readable at a glance; the radio itself stays in the
 * markup, only visually hidden, so click and keyboard behaviour are unchanged.
 */
export const InvoiceActionPicker = ({ actions, value, onChange }) => (
  <Box
    border="1px solid"
    borderColor="gray.200"
    borderRadius="12px"
    px={{ base: 3, md: 4 }}
    py={3}
  >
    <FieldLabel mb={1}>Select Action</FieldLabel>
    {/* <Text fontSize="xs" color="gray.500" mb={3}>
      Only available actions are enabled.
    </Text> */}
    <RadioGroup value={value ?? ''} onChange={onChange}>
      <SimpleGrid
        columns={{ base: 2, lg: Math.min(actions.length, 4) }}
        spacing={{ base: 2, md: 3 }}
      >
        {actions.map((action) => {
          const isSelected = action.value === value;
          // A disabled card explains the blocker rather than what it would do.
          const hint = action.isDisabled
            ? (action.disabledHint ?? action.description)
            : action.description;
          return (
            <Tooltip
              key={action.value}
              label={action.disabledReason ?? ''}
              isDisabled={!action.isDisabled || !action.disabledReason}
              placement="top"
            >
              <Box
                as="label"
                htmlFor={`invoice-action-${action.value}`}
                position="relative"
                display="flex"
                flexDirection="column"
                alignItems="center"
                textAlign="center"
                gap={1}
                h="100%"
                minH={{ base: '96px', md: '104px' }}
                justifyContent="center"
                px={2}
                py={3}
                border="1px solid"
                borderColor={isSelected ? 'reviveGreen' : 'gray.200'}
                bg={
                  isSelected
                    ? 'green.50'
                    : action.isDisabled
                      ? 'gray.50'
                      : 'white'
                }
                borderRadius="12px"
                cursor={action.isDisabled ? 'not-allowed' : 'pointer'}
                _hover={{
                  borderColor: action.isDisabled
                    ? 'gray.200'
                    : isSelected
                      ? 'reviveGreen'
                      : 'primary',
                }}
              >
                {(isSelected || action.isDisabled) && (
                  <Icon
                    as={isSelected ? FaCheckCircle : FaLock}
                    position="absolute"
                    top={3}
                    right={3}
                    w="15px"
                    h="15px"
                    color={isSelected ? 'reviveGreen' : 'gray.400'}
                    aria-hidden={true}
                  />
                )}
                <Icon
                  as={action.icon}
                  w="18px"
                  h="18px"
                  color={action.isDisabled ? 'gray.400' : 'primary'}
                />
                <Text
                  fontSize="sm"
                  fontWeight="600"
                  color={action.isDisabled ? 'gray.500' : 'gray.800'}
                >
                  {action.label}
                </Text>
                {hint && (
                  <Text fontSize="xs" color="gray.500" lineHeight="short">
                    {hint}
                  </Text>
                )}
                <VisuallyHidden>
                  <Radio
                    id={`invoice-action-${action.value}`}
                    value={action.value}
                    isDisabled={action.isDisabled}
                  />
                </VisuallyHidden>
              </Box>
            </Tooltip>
          );
        })}
      </SimpleGrid>
    </RadioGroup>
  </Box>
);
InvoiceActionPicker.propTypes = {
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      /** One line on the card saying what the action does. */
      description: PropTypes.string,
      icon: PropTypes.elementType,
      isDisabled: PropTypes.bool,
      /** Full sentence shown on hover when the action cannot run. */
      disabledReason: PropTypes.string,
      /** Short form of the same reason, shown on the card itself. */
      disabledHint: PropTypes.string,
    }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};
